import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, InputField } from 'src/components/ui'
import { useResetPasswordMutation } from 'src/redux/api'
import AuthShell from 'src/components/elements/auth-shell/AuthShell'

interface FormValues {
  password: string
  confirmPassword: string
}

const apiError = (error: unknown): { code?: string; message?: string } =>
  (error as { data?: { code?: string; message?: string } })?.data ?? {}

// Landing page for the password-reset link from the email. The token lives
// in the query string; a missing or rejected token shows a request-again CTA.
const ResetPassword = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [tokenRejected, setTokenRejected] = useState(false)
  const [resetPassword, { isLoading }] = useResetPasswordMutation()

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { password: '', confirmPassword: '' },
  })

  const onSubmit = async (data: FormValues) => {
    try {
      await resetPassword({ token, password: data.password }).unwrap()
      toast.success(t('reset_password.success'))
      navigate('/login')
    } catch (error) {
      const { code, message } = apiError(error)
      if (code === 'RESET_INVALID') {
        setTokenRejected(true)
        return
      }
      setError('root', { message: message || t('reset_password.unexpected') })
    }
  }

  const tokenUnusable = !token || tokenRejected

  return (
    <AuthShell title={t('reset_password.title')}>
      {tokenUnusable ? (
        <div className="flex flex-col items-start gap-2 p-5 rounded-2xl bg-warning-soft">
          <h2 className="m-0 text-lg font-extrabold leading-6 text-warning">
            {t('reset_password.invalid_token_title')}
          </h2>
          <p className="m-0 text-base leading-6 text-ink">
            {t('reset_password.invalid_token_text')}
          </p>
          <Link
            to="/forgot-password"
            className="min-h-11 inline-flex items-center font-bold text-brand-ink hover:underline"
          >
            {t('reset_password.request_again')}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 self-stretch w-full">
          {errors.root?.message && (
            <p className="m-0 px-3.5 py-2.5 rounded-xl bg-danger-soft text-sm font-bold text-danger">
              {errors.root?.message}
            </p>
          )}
          <div className="flex flex-col gap-4">
            <Controller
              name="password"
              control={control}
              rules={{
                required: t('login.requiredField'),
                minLength: { value: 6, message: t('login.passwordMinLengthError') },
              }}
              render={({ field }) => (
                <InputField
                  {...field}
                  label={t('reset_password.new_password')}
                  type="password"
                  autoComplete="new-password"
                  error={errors.password}
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: t('login.requiredField'),
                validate: (value) =>
                  value === watch('password') || t('reset_password.passwords_mismatch'),
              }}
              render={({ field }) => (
                <InputField
                  {...field}
                  label={t('reset_password.confirm_password')}
                  type="password"
                  autoComplete="new-password"
                  error={errors.confirmPassword}
                />
              )}
            />
          </div>
          <Button size="lg" className="w-full" type="submit" isLoading={isLoading}>
            {t('reset_password.save')}
          </Button>
        </form>
      )}
      <Link
        to="/login"
        className="self-center min-h-11 inline-flex items-center font-bold text-brand-ink hover:underline"
      >
        {t('reset_password.back_to_login')}
      </Link>
    </AuthShell>
  )
}

export default ResetPassword
